import { AspectMaintainer, DatePicker, RangeSlider, Select, Switch } from "@clintonelec/react-storybook";
import {
	DayNightMode, dayNightOptions, dayToNightSensitivityOptions, type DwellTime, dwellTimeOptions, IlluminationMode,
	illuminationModeOptions, type IrLed, irLedOptions, nightToDaySensitivityOptions, type WhiteLedControl,
	whiteLedControlOptions
} from "Data/Objects/Camera";
import { produce } from "immer";
import type { ImageSettingsState } from "Interfaces";
import { type Dispatch, Fragment, memo, type SetStateAction } from "react";

type DayNightProps = {
	localSettings: ImageSettingsState;
	setLocalSettings: Dispatch<SetStateAction<ImageSettingsState>>;
};

function DayNight(props: DayNightProps) {
	const { localSettings, setLocalSettings } = props;
	const {
		dayNightMode, dwellTime, dayTimeStart, dayTimeEnd, dayToNightSensitivity, nightToDaySensitivity,
		illuminationMode, irLed, adaptiveIr, irLedIllumination, antiOverexposure, whiteLedControl, whiteLedIllumination
	} = localSettings.dayNight;

	const isDayMode = dayNightMode === DayNightMode.DAY;
	const isAutoMode = dayNightMode === DayNightMode.AUTO;

	const handleDayNightModeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.dayNightMode = value as DayNightMode;
		}));
	};

	const handleDayTimeStartChange = (value: Date | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.dayTimeStart = value.getTime();
		}));
	};

	const handleDayTimeEndChange = (value: Date | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.dayTimeEnd = value.getTime();
		}));
	};

	const handleDwellTimeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.dwellTime = +value as DwellTime;
		}));
	};

	const handleDayToNightSensitivitySelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.dayToNightSensitivity = +value;
		}));
	};

	const handleNightToDaySensitivitySelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.nightToDaySensitivity = +value;
		}));
	};

	const handleIlluminationModeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.illuminationMode = value as IlluminationMode;
		}));
	};

	const handleIrLedSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.irLed = value as IrLed;
		}));
	};

	const handleAdaptiveIrChecked = (checked: boolean) => {
		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.adaptiveIr = checked;
		}));
	};

	const handleIrLedIlluminationChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.irLedIllumination = value;
		}));
	};

	const handleAntiOverexposureChecked = (checked: boolean) => {
		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.antiOverexposure = checked;
		}));
	};

	const handleWhiteLedControlSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.whiteLedControl = value as WhiteLedControl;
		}));
	};

	const handleWhiteLedIlluminationChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.dayNight.whiteLedIllumination = value;
		}));
	};

	const renderDayTime = () => {
		if (dayNightMode === DayNightMode.SCHEDULE) {
			return (
				<div className="form-row">
					<span>Day Time</span>
					<div className="form-input">
						<DatePicker
							cleanable={ false }
							format="HH:mm"
							defaultValue={ new Date(dayTimeStart) }
							onChange={ handleDayTimeStartChange }
						/>
						<span>~</span>
						<DatePicker
							cleanable={ false }
							format="HH:mm"
							defaultValue={ new Date(dayTimeEnd) }
							onChange={ handleDayTimeEndChange }
						/>
					</div>
				</div>
			);
		}
	};

	const renderIlluminationSetup = () => {
		switch (illuminationMode) {
			case IlluminationMode.IR: {
				return (
					<Fragment>
						<div className="form-row">
							<span>IR LED</span>
							<Select
								allowClear={ false }
								className="form-select"
								disabled={ isDayMode }
								onSelect={ handleIrLedSelect }
								options={ irLedOptions }
								value={ irLed }
							/>
						</div>
						<div className="form-row">
							<span>Adaptive IR</span>
							<div className="form-switch-wrapper">
								<Switch
									checked={ adaptiveIr }
									disabled={ isDayMode }
									onChange={ handleAdaptiveIrChecked }
								/>
							</div>
						</div>
						<div className="form-row">
							<span>IR LED Illumination</span>
							<div className="form-slider-wrapper">
								<div className="image-slider">
									<RangeSlider
										hideInputCharMessage
										inputDisplay
										disabled={ isDayMode }
										max={ 255 }
										min={ 25 }
										onChange={ handleIrLedIlluminationChange }
										value={ irLedIllumination }
									/>
								</div>
							</div>
						</div>
					</Fragment>
				);
			}

			case IlluminationMode.WHITE: {
				return (
					<Fragment>
						<div className="form-row">
							<span>Anti-Overexposure</span>
							<div className="form-switch-wrapper">
								<Switch
									disabled={ isDayMode }
									checked={ antiOverexposure }
									onChange={ handleAntiOverexposureChecked }
								/>
							</div>
						</div>
						<div className="form-row">
							<span>Control</span>
							<Select
								allowClear={ false }
								className="form-select"
								disabled={ isDayMode }
								onSelect={ handleWhiteLedControlSelect }
								options={ whiteLedControlOptions }
								value={ whiteLedControl }
							/>
						</div>
						<div className="form-row">
							<span>White LED Illumination</span>
							<div className="form-slider-wrapper">
								<div className="image-slider">
									<RangeSlider
										hideInputCharMessage
										inputDisplay
										disabled={ isDayMode }
										max={ 200 }
										min={ 1 }
										onChange={ handleWhiteLedIlluminationChange }
										value={ whiteLedIllumination }
									/>
								</div>
							</div>
						</div>
					</Fragment>
				);
			}

			default:
				break;
		}
	};

	return (
		<div className="two-column-card-content">
			<div className="left-content">
				<AspectMaintainer>
					<div className="camera-snapshot"></div>
				</AspectMaintainer>
			</div>
			<div className="right-content">
				<div className="form-row">
					<span>Day / Night Mode</span>
					<Select
						allowClear={ false }
						className="form-select"
						onSelect={ handleDayNightModeSelect }
						options={ dayNightOptions }
						value={ dayNightMode }
					/>
				</div>
				{ renderDayTime() }
				<div className="form-row">
					<span>Dwell Time</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ !isAutoMode }
						onSelect={ handleDwellTimeSelect }
						options={ dwellTimeOptions }
						value={ dwellTime.toString() }
					/>
				</div>
				<div className="form-row">
					<span>Day to Night Sensitivity</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ !isAutoMode }
						onSelect={ handleDayToNightSensitivitySelect }
						options={ dayToNightSensitivityOptions }
						value={ dayToNightSensitivity.toString() }
					/>
				</div>
				<div className="form-row">
					<span>Night to Day Sensitivity</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ !isAutoMode }
						onSelect={ handleNightToDaySensitivitySelect }
						options={ nightToDaySensitivityOptions }
						value={ nightToDaySensitivity.toString() }
					/>
				</div>
				<div className="form-row">&nbsp;</div>
				<div className="form-row">
					<span>Illumination Mode</span>
					<Select
						allowClear={ false }
						className="form-select"
						onSelect={ handleIlluminationModeSelect }
						options={ illuminationModeOptions }
						value={ illuminationMode }
					/>
				</div>
				{ renderIlluminationSetup() }
			</div>
		</div>
	);
}

export default memo(DayNight);
